import { head } from 'framer-motion/client';
import { generateSectionId } from '../../../HelpCenterComponents/ArticleLayout';
import { Link } from 'react-router-dom';

export const LicensingContentBody = ({ activeLeft }) => {
  const contentData = {
    0: {
      sections: [
        {
          type: 'text',
          heading: 'Spatial Grid Licensing & Subscription Programs',
          paragraphs: [
            'Spatial Grid offers a comprehensive and flexible licensing framework designed to support organizations at every stage of their immersive and spatial intelligence journey—from early experimentation to enterprise-scale deployment. Our pricing structure is built around a combination of platform licensing, structured training, and expert consulting services, ensuring customers not only gain access to the technology but are also fully enabled to deliver high-quality immersive experiences.',
            'Each licensing model is designed to balance ease of adoption, operational support, and long-term value creation, while maintaining enterprise-grade reliability and scalability.',
          ],
        },
      ],
    },
    1: {
      sections: [
        {
          type: 'text',
          heading: 'Licensing Model Overview',
        },
        {
          type: 'text',
          paragraphs: [
            'Spatial Grid licensing comprises three core components:',
          ],
        },
        {
          type: 'list',
          items: [
            {
              title: 'Platform Licensing',
              description:
                ' Access to the Spatial Grid platform, tools, and core capabilities required to design, develop, and deploy immersive experiences.',
            },
            {
              title: 'Training & Enablement',
              description:
                ' Structured tool familiarization sessions to ensure users understand platform workflows, best practices, and deployment methodologies.',
            },
            {
              title: 'Consulting & Ongoing Support',
              description:
                ' Continuous remote assistance and expert guidance to help teams build, optimize, and scale immersive experiences effectively.',
            },
          ],
        },
        {
          type: 'text',
          paragraphs: [
            'This integrated approach ensures customers are supported not just technically, but strategically, throughout their engagement with Spatial Grid.',
          ],
        },
      ],
    },
    2: {
      intro:
        'The Single User Monthly Subscription is ideal for individual professionals, innovation teams, pilots, and short-term proof-of-concept initiatives seeking rapid access to Spatial Grids platform capabilities.',
      sections: [
        {
          type: 'text',
          heading: 'Single User Monthly Subscription',
          paragraphs: ['Key Inclusions:'],
        },

        {
          type: 'list',
          items: [
            {
              title: 'Tool Familiarity Training Program (1 Hour)',
              description:
                'A structured onboarding session covering platform fundamentals, workflows, and core capabilities.',
            },
            {
              title: 'Remote Assistance Program (30 minutes per week)',
              description:
                'Ongoing expert support to address technical queries, provide guidance, and assist with experience creation.',
            },
            {
              title: 'Flexible Renewal Structure:',
              description:
                'The monthly subscription can be renewed up to two times, for a maximum engagement period of three months, making it well-suited for short-term evaluations and pilots.',
            },
          ],
        },
      ],
    },
    3: {
      intro:
        'The Single User Annual Subscription is designed for individual creators, researchers, consultants, and internal champions who require sustained access to the platform over a longer duration.',
      sections: [
        {
          type: 'text',
          heading: 'Single User Annual Subscription',
          paragraphs: ['Key Inclusions:'],
        },
        {
          type: 'list',
          items: [
            {
              title: 'Tool Familiarity Training Program (1 Hour)',
              description:
                'Comprehensive onboarding to ensure effective platform usage.',
            },
            {
              title: 'Remote Assistance Program (30 minutes per week)',
              description:
                'Continuous expert support to resolve issues, refine workflows, and enhance experience quality.',
            },
            {
              title: 'Experience Development Support',
              description:
                'Assistance from the Spatial Grid team for the development of two immersive experiences, each up to 3 minutes in duration, ensuring high-quality outputs and faster time-to-value.',
            },
          ],
        },
        {
          type: 'text',
          paragraphs: [
            'This plan provides an optimal balance between independence and expert intervention, enabling users to build confidently while leveraging Spatial Grids domain expertise.',
          ],
        },
      ],
    },
    4: {
      intro:
        'The Multi User Annual Subscription is tailored for enterprise teams, system integrators, and organizations with collaborative development needs.',
      sections: [
        {
          type: 'text',
          heading: 'Multi User Annual Subscription',
          paragraphs: ['Key Inclusions:'],
        },
        {
          type: 'list',
          items: [
            {
              title: 'Five User Licenses:',
              description:
                ' Enables team-based development, collaboration, and parallel workflows.',
            },
            {
              title: 'Tool Familiarity Training Program (1 Hour):',
              description:
                ' Platform onboarding for effective adoption across teams.',
            },
            {
              title: 'Remote Assistance Program (30 minutes per week):',
              description:
                ' Continuous technical and strategic support for experience development.',
            },
            {
              title: 'Experience Development by Spatial Grid Team:',
              description:
                ' Development of two immersive experiences, each up to 3 minutes, delivered by Spatial Grid experts.',
            },
            {
              title: 'Remote Customer Support Manager:',
              description:
                ' Dedicated support with a Next Business Day (NBD) response, ensuring timely resolution of issues and uninterrupted progress.',
            },
          ],
        },
        {
          type: 'text',
          paragraphs: [
            'This plan is ideal for organizations aiming to operationalize immersive solutions across departments or client engagements.',
          ],
        },
      ],
    },
    5: {
      intro:
        'The Three-Year Subscription Program is a strategic, enterprise-grade engagement designed for organizations committed to long-term adoption and scaling of immersive and spatial intelligence solutions.',
      sections: [
        {
          type: 'text',
          heading: 'Three-Year Enterprise Subscription Program',
          paragraphs: ['Key Inclusions:'],
        },
        {
          type: 'list',
          items: [
            {
              title: 'Ten User Licenses for Three Years:',
              description:
                '  Enterprise-wide access enabling sustained development and deployment.',
            },
            {
              title:
                'Quarterly Tool Familiarization & Training Programs (1 Hour each):',
              description:
                'Ongoing enablement to ensure teams stay aligned with platform updates and best practices.',
            },
            {
              title: 'Remote Assistance Program (30 minutes per week):',
              description:
                ' Continuous expert support throughout the engagement period.',
            },
            {
              title: 'Experience Development Support:',
              description:
                ' Development of six immersive experiences, each up to 3 minutes, by the Spatial Grid team.',
            },
            {
              title: 'Dedicated Customer Success Manager:',
              description:
                ' A single point of contact responsible for adoption, optimization, roadmap alignment, and success tracking.',
            },
          ],
        },
        {
          type: 'text',
          paragraphs: [
            'This program is best suited for large enterprises, government bodies, research institutions, and system integrators seeking a long-term strategic partnership with predictable costs and deep engagement.',
          ],
        },
      ],
    },
    6: {
      sections: [
        {
          type: 'text',
          heading: 'Designed for Enterprise Adoption',
          paragraphs: [
            'All Spatial Grid licensing programs are structured to align with global corporate procurement standards, ensuring clarity, transparency, and scalability. Licensing, training, and consulting are bundled thoughtfully to reduce adoption friction and accelerate business outcomes.',
          ],
        },
      ],
    },
    7: {
      sections: [
        {
          type: 'text',
          heading: 'Get in Touch',
          paragraphs: [
            'For detailed licensing discussions, customization options, or enterprise-specific requirements, reach out to the Spatial Grid team. We’ll help you identify the subscription model that best aligns with your goals, scale, and industry needs.',
          ],
        },
      ],
    },
  };

  const currentContent = contentData[activeLeft] || contentData[0];

  return (
    <div className="text-[#FFFFFF]">
      {currentContent?.intro && (
        <p className="font-light font-jakarta text-[15px] leading-relaxed mb-8">
          {currentContent.intro}
        </p>
      )}

      {currentContent?.sections?.map((section, index) => {
        const sectionId = section?.heading
          ? generateSectionId(section.heading)
          : null;

        return (
          <div key={index} className="mb-10">
            {/* Heading (if exists) */}
            {section?.heading && (
              <h2
                id={sectionId}
                className="text-[22px] font-medium text-[#D8D8D8] mb-4 scroll-mt-28"
              >
                {section.heading}
              </h2>
            )}

            {/* 🔹 TEXT SECTION */}
            {section?.type === 'text' &&
              section?.paragraphs?.map((paragraph, pIndex) => (
                <p
                  key={pIndex}
                  className="font-light text-[15px] leading-relaxed mb-4 text-[#E4E4E4]"
                >
                  {paragraph}
                </p>
              ))}

            {/* 🔹 SIMPLE LIST (strings only) */}
            {section?.type === 'list' &&
              Array.isArray(section?.items) &&
              section.items.length > 0 &&
              typeof section.items[0] === 'string' && (
                <ul className="list-disc list-inside space-y-3 text-[15px] font-light text-[#E4E4E4]">
                  {section.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}

            {/* 🔹 DETAILED LIST (objects with title/description) */}
            {section?.type === 'list' &&
              Array.isArray(section?.items) &&
              section.items.length > 0 &&
              typeof section.items[0] === 'object' && (
                <ul className="list-disc list-inside space-y-4 text-[15px] text-[#E4E4E4]">
                  {section.items.map((item, i) => (
                    <li key={i}>
                      <span className="font-semibold text-white">
                        {item?.title}
                      </span>
                      {item?.description && (
                        <span className="font-light">
                          {' '}
                          – {item.description}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        );
      })}
      <p className="font-bold font-jakarta text-[24px] leading-relaxed mb-8 bg-[#242424cc] p-4 rounded-lg mt-6 border border-[#444447] flex items-center justify-between">
        For more information
        <Link to="/contact-us">
          <button
            className="ml-4 px-6 py-3 bg-[#EE2B2A] text-white rounded-md 
                 hover:bg-[#C81F1E] transition-all duration-300 
                 text-[16px] font-semibold"
          >
            Contact Us
          </button>
        </Link>
      </p>
    </div>
  );
};
